import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Label,
  Card,
  CardHeader,
  CardBody,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import ActionButton from '../../../../Components/Common/ActionButton';
import { SkeletonCard } from '../../../../Components/Common/Skeleton';
import useAsyncAction from '../../../../Components/Hooks/useAsyncAction';
import axios from 'axios';
import { api } from '../../../../config';
import notify from '../../../../utils/notify';
import { confirmAction } from '../../../../utils/confirm';
import ModeDeliveryTableFragment from '../ModelDelivery';
import {
  QuantityPricingFields,
  useQuantityDeliveryTabs,
} from '../../../../Components/QuantityTable';
import { PortRestrictionsTable } from '../../../../Components/PortRestriction';
import ClauseCard from './ClauseCard';
import OfferSectionNav from './OfferSectionNav';
import { CounterNegotiationCard } from '../../../../shared/offer-domain/counter';
import { pickLatestCounter } from '../../../../shared/offer-domain/payment';
import {
  getCargoProductTypeName,
  getProcurementProductTypeNames,
  hasMultipleProductTypes,
} from '../../../../shared/offer-domain/constants';
import PaymentComplianceSection, {
  PAYMENT_RESPOND_KEY,
  COMPLIANCE_RESPOND_KEY,
  DELAY_PENALTY_RESPOND_KEY,
} from './PaymentComplianceSection';
import {
  getVisibleProcurementClauses,
  TYPE_RESPONSE_GLOBAL,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
  TYPE_RESPONSE_PRODUCT_SPECIFICATION,
  getOwnClauseResponse,
  hasClauseResponse,
} from '../../../../shared/offer-domain/respond';
import {
  DetailItem,
  SectionHeader,
  SectionCard,
  SectionAlert,
} from '../../../../shared/offer-domain/ui';
import '../../OfferDetail.css';
import { isClauseAccepted } from '../../utils/clauseConfirmation';
import {
  isValidDateValue,
  isValidOperationalToleranceValue,
  OPERATIONAL_TOLERANCE_OPTIONS,
  getOperationalToleranceOptionsCounter,
  resolveOperationalToleranceOption,
  parseOperationalToleranceValue,
  formatDateTime,
  formatOperationalTolerance,
  getCounterDraftValue,
  buildCounterPayload,
  COUNTER_API_FIELD_MAP,
} from '../../../../shared/offer-domain/counter';
import { isOfferDetailReadOnly } from '../../utils/offerEditAccess';
import { formatForFlatpickr } from '../../../../utils/dateformatpickr';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import '../../../../App.css';

const PROPOSAL_VALIDITY_FLATPICKR_OPTIONS = {
  enableTime: true,
  dateFormat: 'd/m/Y H:i',
  time_24hr: true,
};

const createToggleMapKey = setState => key => {
  setState(prev => ({ ...prev, [key]: !prev[key] }));
};

const GLOBAL_NOTES_KEY = 'globalNotes';
const PRODUCT_SPEC_KEY = 'productSpecification';

const RESPOND_TYPE_BY_KEY = {
  [GLOBAL_NOTES_KEY]: TYPE_RESPONSE_GLOBAL,
  [PRODUCT_SPEC_KEY]: TYPE_RESPONSE_PRODUCT_SPECIFICATION,
  [DELAY_PENALTY_RESPOND_KEY]: TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
  [PAYMENT_RESPOND_KEY]: TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  [COMPLIANCE_RESPOND_KEY]: TYPE_RESPONSE_COUNTER_COMPLIANCE,
};

const RequirementTab = ({
  ids,
  form,
  setForm,
  formMain,
  errors,
  error,
  setErrors,
  isLoading,
  qtyDeliveryData,
  token,
  handleInvitationAccept,
  handleSaveAsDraft,
  fetchDataInvitationValidation,
  clauseResponses = {},
  isClauseResponsesLoading = false,
  isClauseResponsesSaving = false,
  saveClauseResponse,
  deleteClauseResponse,
  refreshProcurement,
  confirmedClauseIds = {},
  confirmClause,
  confirmAllClauses,
  clauseConfirmFocusToken = 0,
  ensureClausesConfirmed,
  typeResponseState,
  isDelayPenaltyConfirmed = false,
  delayPenaltyFocusToken = 0,
  ensureDelayPenaltyResolved,
  ensureCargoAlphaOffered,
  cargoAlphaFocusToken = 0,
}) => {
  const [tableData, setTableData] = useState({});
  const [parcelSearch, setParcelSearch] = useState({});
  const [expandedClauses, setExpandedClauses] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [draftNotes, setDraftNotes] = useState({});
  const [editingCounter, setEditingCounter] = useState(null);
  const [counterDraft, setCounterDraft] = useState({
    operationalTolerance: '',
    operationalToleranceOption: '',
    proposalValidityPeriodMax: '',
  });
  const [savedCounters, setSavedCounters] = useState({
    operationalTolerance: null,
    operationalToleranceOption: null,
    proposalValidityPeriodMax: null,
  });
  const [confirmAttention, setConfirmAttention] = useState(false);
  const [clauseShowFilter, setClauseShowFilter] = useState('all');
  const {
    responses: typeResponses,
    isLoading: isTypeResponsesLoading,
    isSaving: isTypeResponsesSaving,
    saveResponse: saveTypeResponse,
    deleteResponse: deleteTypeResponse,
    refresh: refreshTypeResponses,
  } = typeResponseState;

  const { parcelTabs, toggleParcelTab } = useQuantityDeliveryTabs(
    qtyDeliveryData?.items ?? [],
    'delivery'
  );

  useEffect(() => {
    if (isClauseResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(clauseResponses).forEach(([clauseId, response]) => {
      const own = getOwnClauseResponse(response, { isEvaluator: false });
      if (own?.respondNotes?.trim().length > 0) {
        expanded[clauseId] = true;
        drafts[clauseId] = own.respondNotes;
      }
    });

    if (Object.keys(expanded).length === 0) return;

    setExpandedClauses(prev => ({ ...prev, ...expanded }));
    setDraftNotes(prev => ({ ...prev, ...drafts }));
  }, [clauseResponses, isClauseResponsesLoading]);

  useEffect(() => {
    if (!clauseConfirmFocusToken) return undefined;
    setConfirmAttention(true);
    const el = document.getElementById('od-confirm-all-clauses');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus();
    return undefined;
  }, [clauseConfirmFocusToken]);

  useEffect(() => {
    if (!delayPenaltyFocusToken) return;
    document
      .getElementById('od-sec-payment')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [delayPenaltyFocusToken]);

  useEffect(() => {
    if (!cargoAlphaFocusToken) return undefined;
    const section = document.getElementById('od-sec-delivery');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const timer = window.setTimeout(() => {
      const invalidInput = section?.querySelector('input.is-invalid');
      if (invalidInput) {
        invalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalidInput.focus({ preventScroll: true });
        return;
      }
      document
        .getElementById('od-alpha-pricing')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [cargoAlphaFocusToken]);

  useEffect(() => {
    if (isTypeResponsesLoading) return;

    const expanded = {};
    const drafts = {};

    Object.entries(RESPOND_TYPE_BY_KEY).forEach(([key, typeResponse]) => {
      const notes = typeResponses[typeResponse]?.respondNotes;
      if (!notes?.trim()) return;
      expanded[key] = true;
      drafts[key] = notes;
    });

    if (Object.keys(expanded).length === 0) return;

    setExpandedClauses(prev => ({ ...prev, ...expanded }));
    setDraftNotes(prev => ({ ...prev, ...drafts }));
  }, [isTypeResponsesLoading, typeResponses]);

  useEffect(() => {
    setSavedCounters(prev => ({
      ...prev,
      operationalTolerance:
        form.operationalToleranceCounter == null ||
        form.operationalToleranceCounter === ''
          ? null
          : parseOperationalToleranceValue(form.operationalToleranceCounter),
      operationalToleranceOption: resolveOperationalToleranceOption({
        label: form.operationalToleranceOptionsCounterLabel,
        optionsCounter: form.operationalToleranceOptionsCounter,
      }),
      proposalValidityPeriodMax: isValidDateValue(
        form.proposalValidityPeriodMaxCounter
      )
        ? form.proposalValidityPeriodMaxCounter
        : null,
    }));
  }, [
    form.operationalToleranceCounter,
    form.operationalToleranceOptionsCounter,
    form.operationalToleranceOptionsCounterLabel,
    form.proposalValidityPeriodMaxCounter,
  ]);

  useEffect(() => {
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;

      if (
        savedCounters.operationalTolerance != null &&
        next.operationalTolerance === undefined
      ) {
        next.operationalTolerance = true;
        changed = true;
      }
      if (
        savedCounters.proposalValidityPeriodMax != null &&
        next.proposalValidityPeriodMax === undefined
      ) {
        next.proposalValidityPeriodMax = true;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [savedCounters]);

  const handleDraftChange = (clauseId, value) => {
    setDraftNotes(prev => ({ ...prev, [clauseId]: value }));
  };

  const toggleClauseExpand = createToggleMapKey(setExpandedClauses);
  const toggleSectionExpand = createToggleMapKey(setExpandedSections);

  const handleClauseSave = async clauseId => {
    const respondNotes = String(draftNotes[clauseId] ?? '').trim();
    if (!respondNotes) {
      notify.warning('Please enter a response note.');
      return;
    }

    try {
      const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
      const saved = typeResponse
        ? await saveTypeResponse(typeResponse, respondNotes)
        : await saveClauseResponse(clauseId, respondNotes);
      const syncedNotes = saved?.respondNotes ?? respondNotes;
      setExpandedClauses(prev => ({ ...prev, [clauseId]: true }));
      setDraftNotes(prev => ({ ...prev, [clauseId]: syncedNotes }));
      notify.success('Response note saved successfully.');
    } catch {
      notify.error('Failed to save response note. Please try again.');
    }
  };

  const handleClauseCancel = clauseId => {
    const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
    const saved = typeResponse
      ? (typeResponses[typeResponse]?.respondNotes ?? '')
      : (getOwnClauseResponse(clauseResponses[clauseId], {
          isEvaluator: false,
        })?.respondNotes ?? '');
    setDraftNotes(prev => ({ ...prev, [clauseId]: saved }));
    if (!saved.trim()) {
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
    }
  };

  const handleClauseClear = async clauseId => {
    try {
      const typeResponse = RESPOND_TYPE_BY_KEY[clauseId];
      if (typeResponse) {
        await deleteTypeResponse(typeResponse);
      } else {
        await deleteClauseResponse(clauseId);
      }
      setDraftNotes(prev => ({ ...prev, [clauseId]: '' }));
      setExpandedClauses(prev => ({ ...prev, [clauseId]: false }));
      notify.success('Response note deleted successfully.');
    } catch {
      notify.error('Failed to delete response note. Please try again.');
    }
  };

  const handleClauseSecondaryAction = (clauseId, hasSaved) => {
    if (hasSaved) {
      return handleClauseClear(clauseId);
    }
    handleClauseCancel(clauseId);
    return undefined;
  };

  const handleTableDataChange = useCallback((quantityDeliveryTermId, data) => {
    setTableData(prev => ({ ...prev, [quantityDeliveryTermId]: data }));
  }, []);

  const handleQuantityDelivery = async () => {
    const finalDataToSave = Object.values(tableData).flat();
    const payload = { listDyedUndyedAlpha: finalDataToSave };

    try {
      await axios.post(`${api.API_URL}/dmut/set-dyed-undyed`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      void error;
    }
  };

  const validateAndExecute = async (
    actionFn,
    { confirmClauses = false } = {}
  ) => {
    let isValid = false;
    try {
      isValid = await fetchDataInvitationValidation(ids, token);
    } catch {
      return;
    }

    if (isValid) {
      notify.warning('This offer has already been submitted.');
      return;
    }

    if (confirmClauses && ensureClausesConfirmed && !ensureClausesConfirmed()) {
      return;
    }
    if (
      confirmClauses &&
      ensureDelayPenaltyResolved &&
      !ensureDelayPenaltyResolved()
    ) {
      return;
    }
    if (
      confirmClauses &&
      ensureCargoAlphaOffered &&
      !(await ensureCargoAlphaOffered())
    ) {
      return;
    }

    try {
      await actionFn();
    } catch (error) {
      void error;
    }
    try {
      await handleQuantityDelivery();
    } catch (error) {
      void error;
    }
  };

  const [handleExecuteDraft, isExecutingDraft] = useAsyncAction(() =>
    validateAndExecute(handleSaveAsDraft)
  );
  const [handleExecuteOffer, isExecutingOffer] = useAsyncAction(() =>
    validateAndExecute(handleInvitationAccept, { confirmClauses: true })
  );

  const visibleClauses = getVisibleProcurementClauses({
    procurementClauseDetails: form.procurementClauseDetails,
  });

  const isReadOnly = isOfferDetailReadOnly({
    statusOfferingWordingFlag: formMain.statusOfferingWordingFlag,
    statusOfferingWording: formMain.statusOfferingWording,
    submissionStatus: formMain.submissionStatus,
  });

  const startCounterEdit = field => {
    setEditingCounter(field);
    setCounterDraft(prev => ({
      ...prev,
      ...getCounterDraftValue({ field, form, savedCounters }),
    }));
  };

  const cancelCounterEdit = () => {
    setEditingCounter(null);
  };

  const resolveOperationalToleranceCounter = () => {
    const raw =
      form.operationalToleranceCounter ?? savedCounters.operationalTolerance;
    if (raw == null || raw === '') return null;
    return parseOperationalToleranceValue(raw);
  };

  const resolveProposalValidityPeriodMaxCounter = () => {
    const raw =
      form.proposalValidityPeriodMaxCounter ??
      savedCounters.proposalValidityPeriodMax;
    if (!isValidDateValue(raw)) return null;
    return new Date(raw).toISOString();
  };

  const putProcurementCounter = payload =>
    axios.put(
      `${api.API_URL}/procurement/update-procurement-counter/${ids}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

  const handleSaveCounter = async field => {
    const rawValue = String(counterDraft[field] ?? '').trim();
    if (!rawValue) {
      notify.warning('Please enter a counter value.');
      return;
    }

    if (!ids) {
      notify.error(
        'The procurement could not be identified. Return to the list and open it again.'
      );
      return;
    }

    let value;

    if (field === 'proposalValidityPeriodMax') {
      const parsedDate = new Date(rawValue);
      if (Number.isNaN(parsedDate.getTime())) {
        notify.warning(
          'Invalid date format. Choose a date from the date picker.'
        );
        return;
      }
      value = parsedDate.toISOString();
    } else {
      const parsedTolerance = parseOperationalToleranceValue(rawValue);
      if (parsedTolerance == null) {
        notify.warning(
          'Operational tolerance format is invalid. Enter a valid value and try again.'
        );
        return;
      }
      if (!String(counterDraft.operationalToleranceOption ?? '').trim()) {
        notify.warning('Please select an Operational Tolerance option.');
        return;
      }
      value = parsedTolerance;
    }

    const payload = buildCounterPayload({
      field,
      value,
      operationalToleranceCounter: resolveOperationalToleranceCounter(),
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? getOperationalToleranceOptionsCounter(
              counterDraft.operationalToleranceOption
            )
          : getOperationalToleranceOptionsCounter(
              savedCounters.operationalToleranceOption
            ),
      proposalValidityPeriodMaxCounter:
        resolveProposalValidityPeriodMaxCounter(),
    });
    if (!payload) return;

    try {
      await putProcurementCounter(payload);

      const localOption =
        field === 'operationalTolerance'
          ? counterDraft.operationalToleranceOption
          : undefined;

      if (refreshProcurement) {
        await refreshProcurement();
        if (localOption !== undefined) {
          setSavedCounters(prev => ({
            ...prev,
            operationalToleranceOption: localOption,
          }));
        }
      } else {
        setSavedCounters(prev => ({
          ...prev,
          [field]: value,
          ...(localOption !== undefined
            ? { operationalToleranceOption: localOption }
            : {}),
        }));
      }

      setEditingCounter(null);
      notify.success('Counter offer saved successfully');
    } catch {
      notify.error('Failed to save counter offer. Please try again.');
    }
  };

  const handleWithdrawCounter = async field => {
    if (!ids) {
      notify.error(
        'The procurement could not be identified. Return to the list and open it again.'
      );
      return;
    }

    if (!COUNTER_API_FIELD_MAP[field]) return;

    const confirmed = await confirmAction({
      tone: 'danger',
      title: 'Withdraw this counter offer?',
      message:
        'Your proposed counter value will be removed. You can submit a new counter offer afterwards.',
      subject:
        field === 'operationalTolerance'
          ? 'Operational tolerance'
          : field === 'proposalValidityPeriodMax'
            ? 'Proposal validity period'
            : undefined,
      confirmLabel: 'Withdraw counter offer',
    });
    if (!confirmed) return;

    const payload = buildCounterPayload({
      field,
      value: null,
      operationalToleranceCounter: resolveOperationalToleranceCounter(),
      operationalToleranceOptionsCounter:
        field === 'operationalTolerance'
          ? null
          : getOperationalToleranceOptionsCounter(
              savedCounters.operationalToleranceOption
            ),
      proposalValidityPeriodMaxCounter:
        resolveProposalValidityPeriodMaxCounter(),
    });
    if (!payload) return;

    const apiField = COUNTER_API_FIELD_MAP[field];

    try {
      await putProcurementCounter(payload);

      if (refreshProcurement) {
        await refreshProcurement();
        if (field === 'operationalTolerance') {
          setSavedCounters(prev => ({
            ...prev,
            operationalToleranceOption: null,
          }));
        }
      } else {
        setSavedCounters(prev => ({
          ...prev,
          [field]: null,
          ...(field === 'operationalTolerance'
            ? { operationalToleranceOption: null }
            : {}),
        }));
        setForm?.(prev => ({
          ...prev,
          [apiField]: null,
        }));
      }

      setCounterDraft(prev => ({
        ...prev,
        [field]: '',
        ...(field === 'operationalTolerance'
          ? { operationalToleranceOption: '' }
          : {}),
      }));
      setEditingCounter(null);
      setExpandedSections(prev => ({ ...prev, [field]: false }));
      notify.success('Counter offer withdrawn successfully');
    } catch {
      notify.error('Failed to withdraw counter offer. Please try again.');
    }
  };

  const renderProductInfo = () => (
    <>
      <SectionHeader
        title='Product Specification/ Quantity'
        subtitle='Review product specification and quantity'
      />
      <Row className='gy-4'>
        <Col lg={4}>
          <DetailItem label='Product Name' value={form.productName} />
        </Col>
        <Col lg={4}>
          <DetailItem
            label='Product Type'
            value={getProcurementProductTypeNames(form) || form.productTypename}
          />
        </Col>
        <Col lg={12}>
          <DetailItem
            label='Product Specification'
            value={form.productSpecification}
            isHtml
          />
        </Col>
      </Row>
      <Row className='mt-3'>
        <ClauseCard
          clause={{
            id: PRODUCT_SPEC_KEY,
            clauseTitle: 'Product Specification Clarification',
            clauseDescription: '',
          }}
          clauseResponses={{
            [PRODUCT_SPEC_KEY]:
              typeResponses[TYPE_RESPONSE_PRODUCT_SPECIFICATION],
          }}
          draftNotes={draftNotes}
          expandedClauses={expandedClauses}
          formMain={formMain}
          isClauseResponsesSaving={isTypeResponsesSaving}
          isClauseResponsesLoading={isTypeResponsesLoading}
          handleDraftChange={handleDraftChange}
          handleClauseSecondaryAction={handleClauseSecondaryAction}
          handleClauseSave={handleClauseSave}
          toggleClauseExpand={toggleClauseExpand}
          canRespond={!isReadOnly}
          showDescription={false}
        />
      </Row>
    </>
  );

  const renderCounterToleranceSection = () => (
    <Row className='mb-4'>
      <Col sm={12}>
        <CounterNegotiationCard
          title='Operational Tolerance'
          subtitle='Review operational tolerance requirements'
          proposeCounterLabel='Propose Counter Value Operation Tolerance (±)'
          partyType={form.partyType}
          label='Operational Tolerance'
          currentDisplay={formatOperationalTolerance(form.operationalTolerance)}
          counterDisplay={
            isValidOperationalToleranceValue(savedCounters.operationalTolerance)
              ? formatOperationalTolerance(savedCounters.operationalTolerance)
              : null
          }
          partyTypeCounter={savedCounters.operationalToleranceOption || null}
          isExpanded={Boolean(expandedSections['operationalTolerance'])}
          onToggleExpand={() => toggleSectionExpand('operationalTolerance')}
          isEditing={editingCounter === 'operationalTolerance'}
          editable={!isReadOnly}
          isDraftDirty={
            String(counterDraft.operationalTolerance) !==
              String(savedCounters.operationalTolerance ?? '') ||
            String(counterDraft.operationalToleranceOption) !==
              String(savedCounters.operationalToleranceOption ?? '')
          }
          onStartEdit={() => startCounterEdit('operationalTolerance')}
          onCancel={cancelCounterEdit}
          onSave={() => handleSaveCounter('operationalTolerance')}
          onDelete={() => handleWithdrawCounter('operationalTolerance')}
        >
          <div className='d-flex flex-column gap-4 p-1'>
            <div>
              <Label
                htmlFor='customToleranceInput'
                className='form-label text-muted small fw-semibold mb-2'
              >
                Counter Operation Tolerance (±)
              </Label>
              <div
                className='input-group input-group-sm'
                style={{ maxWidth: '240px' }}
              >
                <Input
                  id='customToleranceInput'
                  type='number'
                  min='0'
                  step='0.01'
                  placeholder='0.00'
                  value={counterDraft.operationalTolerance}
                  onChange={e =>
                    setCounterDraft(prev => ({
                      ...prev,
                      operationalTolerance: e.target.value,
                    }))
                  }
                  className='text-dark fw-medium'
                />
                <span className='input-group-text bg-light text-muted fw-semibold px-3'>
                  %
                </span>
              </div>
            </div>

            <hr className='text-muted my-0 opacity-25' />

            <div>
              <Label className='form-label text-muted small fw-semibold mb-2'>
                Counter Operation Tolerance Options
              </Label>
              <div className='d-flex flex-wrap gap-2'>
                {OPERATIONAL_TOLERANCE_OPTIONS.map(option => {
                  const isChecked =
                    counterDraft.operationalToleranceOption === option.value;

                  return (
                    <div
                      key={option.value}
                      role='presentation'
                      className={`border rounded-3 px-3 py-2 mb-0 d-flex align-items-center gap-2 transition-all ${
                        isChecked
                          ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                          : 'border-light bg-light text-dark fw-medium'
                      }`}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() =>
                        setCounterDraft(prev => ({
                          ...prev,
                          operationalToleranceOption: option.value,
                        }))
                      }
                    >
                      <Input
                        className='form-check-input ms-0 mt-0 flex-shrink-0'
                        type='radio'
                        id={`operationalToleranceOption-${option.value}`}
                        name='operationalToleranceOption'
                        value={option.value}
                        checked={isChecked}
                        onChange={e =>
                          setCounterDraft(prev => ({
                            ...prev,
                            operationalToleranceOption: e.target.value,
                          }))
                        }
                        style={{ cursor: 'pointer' }}
                      />
                      <Label
                        className='form-check-label fs-13 mb-0 text-current cursor-pointer'
                        htmlFor={`operationalToleranceOption-${option.value}`}
                        style={{ cursor: 'pointer' }}
                      >
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CounterNegotiationCard>
      </Col>
    </Row>
  );

  const renderCounterValidToSection = () => (
    <Row className='mb-4'>
      <Col sm={12}>
        <CounterNegotiationCard
          title='Validity Period'
          subtitle='Review validity period requirements'
          proposeCounterLabel='Propose Counter Value Validity To (Jakarta Local Time)'
          label='Valid To (Jakarta Local Time)'
          currentDisplay={formatDateTime(form.proposalValidityPeriodMax)}
          extraFields={[
            {
              label: 'Submission Deadline (Jakarta Local Time)',
              value: formatDateTime(form.proposalSubmissionDeadline),
            },
          ]}
          counterDisplay={
            isValidDateValue(savedCounters.proposalValidityPeriodMax)
              ? formatDateTime(savedCounters.proposalValidityPeriodMax)
              : null
          }
          isExpanded={Boolean(expandedSections['proposalValidityPeriodMax'])}
          onToggleExpand={() =>
            toggleSectionExpand('proposalValidityPeriodMax')
          }
          isEditing={editingCounter === 'proposalValidityPeriodMax'}
          editable={!isReadOnly}
          isDraftDirty={
            String(counterDraft.proposalValidityPeriodMax) !==
            String(savedCounters.proposalValidityPeriodMax ?? '')
          }
          onStartEdit={() => startCounterEdit('proposalValidityPeriodMax')}
          onCancel={cancelCounterEdit}
          onSave={() => handleSaveCounter('proposalValidityPeriodMax')}
          onDelete={() => handleWithdrawCounter('proposalValidityPeriodMax')}
        >
          <Flatpickr
            className='form-control form-control-sm'
            options={PROPOSAL_VALIDITY_FLATPICKR_OPTIONS}
            value={
              formatForFlatpickr(counterDraft.proposalValidityPeriodMax) || ''
            }
            onChange={dates => {
              setCounterDraft(prev => ({
                ...prev,
                proposalValidityPeriodMax: dates[0]?.toISOString() ?? '',
              }));
            }}
          />
        </CounterNegotiationCard>
      </Col>
    </Row>
  );

  const renderQuantityDeliverySection = () => (
    <SectionCard>
      <SectionHeader
        title='Quantity & Delivery Time'
        subtitle='Review delivery requirements and submit your offer for each cargo'
      />

      <SectionAlert title='Counter Offer Impact!'>
        <div className='text-muted small'>
          You may propose modified terms (counter offer) specifications that
          differ from the original requirements. Please note that any deviation
          from the buyer&apos;s requirements will be taken into consideration
          during the tender evaluation process.
        </div>
      </SectionAlert>

      {isLoading ? (
        <SkeletonCard lines={5} className='border mb-0' />
      ) : error ? (
        <div className='alert alert-danger text-center mb-0'>{error}</div>
      ) : qtyDeliveryData.items.length > 0 ? (
        <div className='d-flex flex-column gap-4'>
          {qtyDeliveryData.items.map((data, index) => {
            const activeTab = parcelTabs[data.id] || 'delivery';
            const unitLabel =
              data.deliveryUnit || `Cargo ${String.fromCharCode(65 + index)}`;
            return (
              <Card
                className='border shadow-sm mb-0 overflow-visible'
                key={`${data.id}-${index}`}
              >
                <div className='bg-light border-bottom px-4 py-3 d-flex align-items-center justify-content-between gap-3 flex-wrap'>
                  <h6 className='fw-semibold text-dark mb-0'>
                    Delivery Unit: {unitLabel}
                    {hasMultipleProductTypes(form) && (
                      <span className='text-muted fw-normal ms-2'>
                        ({getCargoProductTypeName(form, data) || '-'})
                      </span>
                    )}
                  </h6>
                  <div
                    className='position-relative'
                    style={{ width: '260px', maxWidth: '100%' }}
                  >
                    <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-2' />
                    <Input
                      type='text'
                      placeholder='Search incoterm route / port location'
                      className='ps-5 rounded-3'
                      style={{ height: '32px' }}
                      value={parcelSearch[data.id] || ''}
                      onChange={e =>
                        setParcelSearch(prev => ({
                          ...prev,
                          [data.id]: e.target.value,
                        }))
                      }
                      aria-label={`Search delivery unit ${unitLabel}`}
                    />
                    {parcelSearch[data.id] ? (
                      <button
                        type='button'
                        className='btn btn-link btn-sm p-0 position-absolute top-50 translate-middle-y end-0 me-2 text-muted'
                        onClick={() =>
                          setParcelSearch(prev => ({ ...prev, [data.id]: '' }))
                        }
                        aria-label='Clear search'
                      >
                        <i className='ri-close-line' aria-hidden='true' />
                      </button>
                    ) : null}
                  </div>
                </div>
                <CardHeader className='bg-white p-0 border-bottom'>
                  <Nav tabs className='nav-tabs m-0'>
                    <NavItem>
                      <NavLink
                        tag='button'
                        type='button'
                        className={
                          activeTab === 'delivery'
                            ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                            : 'text-uppercase fw-semibold text-muted py-3 px-4'
                        }
                        onClick={() => toggleParcelTab(data.id, 'delivery')}
                        style={{ cursor: 'pointer' }}
                      >
                        Delivery Unit
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        tag='button'
                        type='button'
                        className={
                          activeTab === 'pricing'
                            ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                            : 'text-uppercase fw-semibold text-muted py-3 px-4'
                        }
                        onClick={() => toggleParcelTab(data.id, 'pricing')}
                        style={{ cursor: 'pointer' }}
                      >
                        Pricing
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody className='p-0'>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId='delivery'>
                      <ModeDeliveryTableFragment
                        form={form}
                        setForm={setForm}
                        errors={errors}
                        error={error}
                        setErrors={setErrors}
                        quantityDeliveryTermId={data.id}
                        cargo={data}
                        token={token}
                        onDataChange={handleTableDataChange}
                        formMain={formMain}
                        searchTerm={parcelSearch[data.id] || ''}
                        setSearchTerm={value =>
                          setParcelSearch(prev => ({
                            ...prev,
                            [data.id]: value,
                          }))
                        }
                        embedded
                      />
                    </TabPane>
                    <TabPane tabId='pricing'>
                      <div className='p-4'>
                        <QuantityPricingFields
                          parcel={data}
                          showDeliveryUnit={false}
                        />
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className='text-center py-5 text-muted border rounded-3 bg-light-subtle'>
          No data available
        </div>
      )}

      <hr className='my-4 border-secondary border-dashed' />

      <Row>
        <ClauseCard
          clause={{
            id: GLOBAL_NOTES_KEY,
            clauseTitle: 'Notes',
            clauseDescription: form?.globalNotes,
          }}
          clauseResponses={{
            [GLOBAL_NOTES_KEY]: typeResponses[TYPE_RESPONSE_GLOBAL],
          }}
          draftNotes={draftNotes}
          expandedClauses={expandedClauses}
          formMain={formMain}
          isClauseResponsesSaving={isTypeResponsesSaving}
          isClauseResponsesLoading={isTypeResponsesLoading}
          handleDraftChange={handleDraftChange}
          handleClauseSecondaryAction={handleClauseSecondaryAction}
          handleClauseSave={handleClauseSave}
          toggleClauseExpand={toggleClauseExpand}
          canRespond={!isReadOnly}
        />
      </Row>

      <hr className='my-4 border-secondary border-dashed' />

      <div className='mt-2'>
        <h5 className='fw-bold text-dark mb-3'>Port Restrictions</h5>
        <PortRestrictionsTable
          ports={form?.portRestriction}
          procurementId={ids}
        />
      </div>
    </SectionCard>
  );

  const renderClauseSection = () => {
    const isClauseConfirmed = clause =>
      isClauseAccepted(clause, clauseResponses, confirmedClauseIds);
    const mandatoryClauses = visibleClauses.filter(
      clause => clause.isShowToDMUT === true
    );
    const optionalClauses = visibleClauses.filter(
      clause => clause.isShowToDMUT !== true
    );
    const acceptedCount = visibleClauses.filter(isClauseConfirmed).length;
    const mandatoryAcceptedCount =
      mandatoryClauses.filter(isClauseConfirmed).length;
    const optionalAcceptedCount =
      optionalClauses.filter(isClauseConfirmed).length;
    const allClausesAccepted =
      visibleClauses.length > 0 && acceptedCount === visibleClauses.length;
    const mandatoryAccepted =
      mandatoryClauses.length > 0 &&
      mandatoryAcceptedCount === mandatoryClauses.length;
    const progressPct =
      visibleClauses.length === 0
        ? 0
        : Math.round((acceptedCount / visibleClauses.length) * 100);
    const displayedClauses =
      clauseShowFilter === 'mandatory'
        ? mandatoryClauses
        : clauseShowFilter === 'nonMandatory'
          ? optionalClauses
          : visibleClauses;
    const viewOptions = [
      {
        key: 'all',
        label: 'All',
        count: visibleClauses.length,
      },
      {
        key: 'mandatory',
        label: 'Mandatory',
        count: mandatoryClauses.length,
      },
      {
        key: 'nonMandatory',
        label: 'Optional',
        count: optionalClauses.length,
      },
    ];
    const activeViewLabel =
      viewOptions.find(option => option.key === clauseShowFilter)?.label ??
      'All';

    return (
      <SectionCard>
        <SectionHeader
          title='Clause Details'
          subtitle='Review procurement terms and record confirmation or response notes as applicable.'
          countLabel={`${acceptedCount}/${visibleClauses.length} confirmed`}
        />

        <Row className='g-3 mb-4'>
          <Col md={6} className='d-flex'>
            <SectionAlert
              className='mb-0 w-100'
              icon='ri-error-warning-line'
              title='Response Notice'
            >
              <div className='text-dark fs-13'>
                Mandatory clauses are subject to confirmation only. Optional
                clauses may be confirmed as stated, or accompanied by a response
                note where alternative terms are proposed.
              </div>
            </SectionAlert>
          </Col>
          <Col md={6} className='d-flex'>
            <SectionAlert
              tone='info'
              icon='ri-information-line'
              className='mb-0 w-100'
              title='Specification Compliance Notice'
            >
              <div className='text-dark fs-13'>
                Any information that does not align with Pertamina&apos;s
                established standards may be subject to further evaluation and
                acceptance at Pertamina&apos;s discretion.
              </div>
            </SectionAlert>
          </Col>
        </Row>

        {visibleClauses.length > 0 ? (
          <div
            id='od-confirm-all-clauses'
            tabIndex={-1}
            className={`od-clause-toolbar${
              confirmAttention ? ' od-confirm-attention' : ''
            }`}
          >
            <div className='od-clause-toolbar__row'>
              <div className='od-clause-progress'>
                <div className='od-clause-progress__label'>
                  Confirmation status
                </div>
                <div className='od-clause-progress__track'>
                  <div
                    className='od-clause-progress__bar'
                    role='progressbar'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressPct}
                    aria-label='Clause confirmation status'
                  >
                    <div
                      className={`od-clause-progress__fill${
                        allClausesAccepted ? ' is-complete' : ''
                      }`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <span className='od-clause-progress__count'>
                    {acceptedCount}/{visibleClauses.length}
                  </span>
                </div>
                <div className='od-clause-progress__meta'>
                  Mandatory {mandatoryAcceptedCount}/{mandatoryClauses.length} ·
                  Optional {optionalAcceptedCount}/{optionalClauses.length}
                </div>
              </div>

              {!isReadOnly ? (
                <div className='d-flex flex-wrap gap-2'>
                  <ActionButton
                    variant='secondary'
                    icon='ri-checkbox-line'
                    disabled={
                      isClauseResponsesSaving ||
                      mandatoryClauses.length === 0 ||
                      mandatoryAccepted
                    }
                    onClick={() => {
                      confirmAllClauses?.(mandatoryClauses);
                      setConfirmAttention(false);
                    }}
                  >
                    Confirm mandatory
                  </ActionButton>
                  <ActionButton
                    variant='primary'
                    icon='ri-checkbox-multiple-line'
                    disabled={isClauseResponsesSaving || allClausesAccepted}
                    onClick={() => {
                      confirmAllClauses?.(visibleClauses);
                      setConfirmAttention(false);
                    }}
                  >
                    Confirm all
                  </ActionButton>
                </div>
              ) : null}
            </div>

            <hr className='od-clause-toolbar__divider' />

            <div className='od-clause-toolbar__row'>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <span className='od-clause-view__label'>View</span>
                <div
                  className='od-clause-seg'
                  role='group'
                  aria-label='Filter clauses by type'
                >
                  {viewOptions.map(option => {
                    const isActive = clauseShowFilter === option.key;
                    return (
                      <button
                        key={option.key}
                        type='button'
                        className={`od-clause-seg__btn${
                          isActive ? ' is-active' : ''
                        }`}
                        aria-pressed={isActive}
                        onClick={() => setClauseShowFilter(option.key)}
                      >
                        <span>{option.label}</span>
                        <span className='od-clause-seg__count'>
                          {option.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <span className='od-clause-view__hint'>
                Showing {displayedClauses.length} · {activeViewLabel}
              </span>
            </div>
          </div>
        ) : null}

        {visibleClauses.length > 0 ? (
          displayedClauses.length > 0 ? (
            <Row>
              {displayedClauses.map((clause, index) => (
                <ClauseCard
                  key={clause.id}
                  index={index + 1}
                  clause={clause}
                  clauseResponses={clauseResponses}
                  draftNotes={draftNotes}
                  expandedClauses={expandedClauses}
                  formMain={formMain}
                  isClauseResponsesSaving={isClauseResponsesSaving}
                  isClauseResponsesLoading={isClauseResponsesLoading}
                  handleDraftChange={handleDraftChange}
                  handleClauseSecondaryAction={handleClauseSecondaryAction}
                  handleClauseSave={handleClauseSave}
                  toggleClauseExpand={toggleClauseExpand}
                  canRespond={!isReadOnly}
                  allowConfirm
                  isConfirmed={Boolean(confirmedClauseIds[clause.id])}
                  onConfirm={() => confirmClause?.(clause.id)}
                />
              ))}
            </Row>
          ) : (
            <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
              No {activeViewLabel.toLowerCase()} clauses to show.
            </div>
          )
        ) : (
          <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
            No clause data available.
          </div>
        )}
      </SectionCard>
    );
  };

  const hasTypeNote = typeResponse =>
    hasClauseResponse(typeResponses[typeResponse]?.respondNotes);
  const respondedClauseCount = visibleClauses.filter(clause =>
    hasClauseResponse(
      getOwnClauseResponse(clauseResponses[clause.id], { isEvaluator: false })
        ?.respondNotes
    )
  ).length;
  const hasValidityCounter = savedCounters.proposalValidityPeriodMax != null;
  const hasToleranceCounter = savedCounters.operationalTolerance != null;
  const hasDelayPenaltyCounter = Boolean(
    pickLatestCounter(form.counterDelayPenaltyTerm, formMain?.id)
  );
  const hasPaymentCounter = Boolean(
    pickLatestCounter(form.counterPaymentTerm, formMain?.id)
  );
  const hasComplianceCounter = Boolean(
    pickLatestCounter(form.counterCompliance, formMain?.id)
  );
  const counterCount = [
    hasValidityCounter,
    hasToleranceCounter,
    hasDelayPenaltyCounter,
    hasPaymentCounter,
    hasComplianceCounter,
  ].filter(Boolean).length;
  const noteCount =
    respondedClauseCount +
    [
      TYPE_RESPONSE_GLOBAL,
      TYPE_RESPONSE_PRODUCT_SPECIFICATION,
      TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
      TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
      TYPE_RESPONSE_COUNTER_COMPLIANCE,
    ].filter(hasTypeNote).length;

  const navSections = [
    {
      id: 'od-sec-product',
      label: 'Product Specification',
      icon: 'ri-flask-line',
      flag: hasTypeNote(TYPE_RESPONSE_PRODUCT_SPECIFICATION)
        ? 'done'
        : undefined,
    },
    {
      id: 'od-sec-validity',
      label: 'Validity Period',
      icon: 'ri-calendar-check-line',
      flag: hasValidityCounter ? 'counter' : undefined,
    },
    {
      id: 'od-sec-tolerance',
      label: 'Operational Tolerance',
      icon: 'ri-scales-3-line',
      flag: hasToleranceCounter ? 'counter' : undefined,
    },
    {
      id: 'od-sec-delivery',
      label: 'Quantity & Delivery',
      icon: 'ri-ship-2-line',
      meta: isLoading ? undefined : `${qtyDeliveryData.items.length} cargo`,
    },
    {
      id: 'od-sec-clauses',
      label: 'Clause Details',
      icon: 'ri-file-list-3-line',
      meta:
        visibleClauses.length > 0
          ? `${respondedClauseCount}/${visibleClauses.length}`
          : undefined,
    },
    {
      id: 'od-sec-payment',
      label: 'Delay Penalty, Payment & Compliance',
      icon: 'ri-bank-card-line',
      flag:
        hasDelayPenaltyCounter || hasPaymentCounter || hasComplianceCounter
          ? 'counter'
          : isDelayPenaltyConfirmed ||
              hasTypeNote(TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM) ||
              hasTypeNote(TYPE_RESPONSE_COUNTER_PAYMENT_TERM) ||
              hasTypeNote(TYPE_RESPONSE_COUNTER_COMPLIANCE)
            ? 'done'
            : undefined,
    },
  ];

  return (
    <React.Fragment>
      <OfferSectionNav
        sections={navSections}
        counterCount={counterCount}
        noteCount={noteCount}
        isReadOnly={isReadOnly}
      />

      <div id='od-sec-product' className='od-section'>
        <SectionCard>{renderProductInfo()}</SectionCard>
      </div>

      <div id='od-sec-validity' className='od-section'>
        {renderCounterValidToSection()}
      </div>

      <div id='od-sec-tolerance' className='od-section'>
        {renderCounterToleranceSection()}
      </div>

      <div id='od-sec-delivery' className='od-section'>
        {renderQuantityDeliverySection()}
      </div>

      <div id='od-sec-clauses' className='od-section'>
        {renderClauseSection()}
      </div>

      <div id='od-sec-payment' className='od-section'>
        <PaymentComplianceSection
          form={form}
          token={token}
          editable={!isReadOnly}
          canRespond={!isReadOnly}
          formMain={formMain}
          refreshProcurement={refreshProcurement}
          refreshTypeResponses={refreshTypeResponses}
          isDelayPenaltyConfirmed={isDelayPenaltyConfirmed}
          typeResponses={{
            [DELAY_PENALTY_RESPOND_KEY]:
              typeResponses[TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM],
            [PAYMENT_RESPOND_KEY]:
              typeResponses[TYPE_RESPONSE_COUNTER_PAYMENT_TERM],
            [COMPLIANCE_RESPOND_KEY]:
              typeResponses[TYPE_RESPONSE_COUNTER_COMPLIANCE],
          }}
          draftNotes={draftNotes}
          expandedClauses={expandedClauses}
          isRespondSaving={isTypeResponsesSaving}
          isRespondLoading={isTypeResponsesLoading}
          handleDraftChange={handleDraftChange}
          handleClauseSecondaryAction={handleClauseSecondaryAction}
          handleClauseSave={handleClauseSave}
          toggleClauseExpand={toggleClauseExpand}
        />
      </div>

      {!isReadOnly && (
        <div className='req-actionbar flex-wrap d-print-none'>
          <ActionButton
            variant='secondary'
            icon='ri-draft-line'
            disabled={isExecutingOffer}
            onClick={handleExecuteDraft}
          >
            Save as Draft
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            disabled={isExecutingDraft}
            onClick={handleExecuteOffer}
          >
            Submit Offer
          </ActionButton>
        </div>
      )}
    </React.Fragment>
  );
};

export default RequirementTab;
